import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { api, money, regCode, EDITION_STYLE } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [records, setRecords] = useState(null);

  useEffect(() => { api.get("/registries/mine").then(({ data }) => setRecords(data)).catch(() => setRecords([])); }, []);

  const paid = (records || []).filter((r) => r.payment_status === "PAID");

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-10 py-24" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="hy-label">Customer Dashboard</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-5">
            Welcome, <span className="hy-gold-text">{user?.name?.split(" ")[0]}</span>
          </h1>
        </div>
        <Link to="/create" className="hy-btn px-7 py-4 text-xs uppercase flex items-center gap-2" data-testid="dashboard-create-btn">
          <Plus className="w-4 h-4" /> New Registration
        </Link>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 mt-14">
        <Stat label="Confirmed Records" value={paid.length} testid="stat-confirmed" />
        <Stat label="Pending Payment" value={(records || []).length - paid.length} testid="stat-pending" />
        <Stat label="Total Contributed" value={money(paid.reduce((s, r) => s + r.amount, 0))} testid="stat-total" />
      </div>

      <h2 className="font-display text-2xl text-amber-100 mt-20">Your registry records</h2>
      <div className="hy-rule my-6" />

      {records === null ? (
        <Loader2 className="w-6 h-6 animate-spin text-amber-300 mt-10" />
      ) : records.length === 0 ? (
        <div className="hy-card p-12 text-center" data-testid="dashboard-empty">
          <p className="font-serif-accent italic text-slate-400 text-lg">No registry records yet.</p>
          <Link to="/create" className="hy-btn inline-block px-8 py-4 text-xs uppercase mt-8">Create your first record</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="dashboard-records">
          {records.map((r) => (
            <div key={r.registry_id} className="hy-card p-7" data-testid={`record-card-${r.registry_id}`}>
              <div className="flex items-center justify-between gap-3">
                <span className={`text-[0.55rem] uppercase tracking-[0.2em] px-3 py-1 border rounded-full ${EDITION_STYLE[r.edition]}`}>{r.edition}</span>
                <span className={`text-[0.6rem] uppercase tracking-widest ${r.payment_status === "PAID" ? "text-emerald-400" : "text-amber-400"}`}>
                  {r.payment_status === "PAID" ? "Registered" : "Payment pending"}
                </span>
              </div>
              <div className="font-display text-lg text-amber-200 mt-5">
                {r.number_confirmed ? regCode(r.registry_number) : `Provisional #${String(r.registry_number).padStart(3, "0")}`}
              </div>
              <div className="text-sm text-white mt-3">{r.dedication_name}</div>
              <div className="font-mono-hy text-[0.7rem] text-slate-500 mt-2">{r.registry_type} · {r.location_display}</div>
              <div className="hy-rule my-6" />
              <div className="flex gap-3">
                {r.payment_status === "PAID" ? (
                  <>
                    <Link to={`/registry/${r.registry_id}`} className="hy-btn-ghost px-4 py-2.5 text-[0.65rem] uppercase" data-testid={`view-${r.registry_id}`}>View</Link>
                    <Link to={`/certificate/${r.registry_id}`} className="hy-btn px-4 py-2.5 text-[0.65rem] uppercase" data-testid={`cert-${r.registry_id}`}>Certificate</Link>
                  </>
                ) : (
                  <Link to={`/checkout/${r.registry_id}`} className="hy-btn px-4 py-2.5 text-[0.65rem] uppercase" data-testid={`pay-${r.registry_id}`}>
                    Complete payment · {money(r.amount)}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value, testid }) => (
  <div className="hy-glass p-7" data-testid={testid}>
    <div className="hy-label">{label}</div>
    <div className="font-display text-3xl text-amber-200 mt-3">{value}</div>
  </div>
);
